import * as THREE from 'three';
import { C, SEASON_LEAF } from '../core/palette.js';
import { box, cyl, sphere, textSprite } from '../core/meshkit.js';
import { BreadBoi } from '../entities/breadBoi.js';
import { Townsperson, checkNpcClick } from '../entities/townsfolk.js';
import { createAmbient } from '../entities/ambient.js';
import { WeatherSystem, getWeatherState, weatherName, weatherIcon } from '../core/weather.js';
import { getSeason, isNight } from '../core/world.js';
import { HUD } from '../ui/hud.js';
import { questsForGiver, acceptQuest, turnIn, isReady, objectiveProgress, QUESTS } from '../core/quests.js';
import { FishingGame } from '../minigames/fishing.js';

// Sandy Toes Beach — a sunny cove south-east of Toast Town.
// Walk the sand to fish off the jetty, or visit the Aquarium.
//
// The beach is an ARC, not a disc: land behind you, open sea in front. Both of its
// boundaries are things you can see. Seaward, you stop at the water's edge; landward,
// a vegetated dune ridge stands exactly on the walk limit. Nowhere can you see sand
// you are not allowed to stand on — that was the old beach's worst tell (it let you
// stroll out across the sea, because the clamp was a rectangle and the sea was not).

export function createBeachScene(){
  return () => new BeachScene();
}

const GROUND_R = 34;      // how far the sand is drawn
const WALK_R   = 26;      // how far you may walk — the dune ridge stands here
const DUNE_R   = 26.4;

// The shoreline: z = SHORE_Z - SHORE_BOW·x². The sea reaches furthest inland at the
// centre and the land runs out into two headland arms, which is what makes a cove.
const SHORE_Z   = -9;
const SHORE_BOW = 0.012;

const shoreZ = (x) => SHORE_Z - SHORE_BOW * x * x;

const LAMP_ON  = 0xFFE9A8;   // lit glass, and the light it throws
const LAMP_OFF = 0xD8D2C2;   // cold glass by day

// Where the shoreline crosses a circle of radius r (the ends of the sand arc).
function shoreX(r){
  let lo = 0, hi = r;
  for (let i = 0; i < 48; i++){
    const m = (lo + hi) / 2;
    const z = shoreZ(m);
    if (m * m + z * z > r * r) hi = m; else lo = m;
  }
  return lo;
}

class BeachScene {
  constructor(){
    this.state = 'explore';
    this.panels = [];
    this.lamps = [];

    this.season = getSeason();
    this.leaf = SEASON_LEAF[this.season] || C.sprout;

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(C.sky);
    // The sea must dissolve INTO the sky, or its far edge reads as a teal wall
    // standing at the back of the level. Fog does the horizon.
    this.scene.fog = new THREE.Fog(C.sky, 34, 108);

    this.camera = new THREE.PerspectiveCamera(50, window.innerWidth/window.innerHeight, 0.1, 400);
    this.camDist = 18;
    this.camTarget = new THREE.Vector3(0, 1.5, 0);
    this.azimuth = 0; this.polar = 0.5;

    this._buildLights();
    this._buildSea();
    this._buildGround();
    this._buildDunes();
    this._buildBoardwalk();
    this._buildJetty();
    this._buildProps();
    this._buildAquarium();

    this.bread = new BreadBoi();
    this.bread.root.position.set(0, 0, 10);
    this.bread.root.rotation.y = Math.PI;          // facing the sea
    this.scene.add(this.bread.root);

    this.angler = this._makeAngler();
    this.scene.add(this.angler.root);

    this.ambient = createAmbient({
      center: new THREE.Vector3(0, 0, -2), radius: 22,
      butterflies: 1, birds: 1, seagulls: 6
    });
    this.scene.add(this.ambient.group);

    this.weather = new WeatherSystem(this.scene, { center: new THREE.Vector3(0, 0, 0), radius: WALK_R });
    this._applyWeather();

    this.hotspots = [
      { pos: this.angler.root.position.clone(), r: 2.5, label: '🗣 talk to Angler Andy', action: () => this._talkToAngler() },
      { pos: new THREE.Vector3(0, 0, 24), r: 3.0, label: '← back to Toast Town', action: () => this.manager.goTo('town', { from: 'beach' }) },
      { pos: new THREE.Vector3(-11, 0, 2.0), r: 2.6, label: 'enter the Aquarium', action: () => this.manager.goTo('aquarium', { from: 'beach' }) },
      { pos: new THREE.Vector3(0, 0, -7.4), r: 2.8, label: '🎣 fish off the jetty', action: () => this._startFishing() }
    ];
    this.nearSpot = null;
    this._updateLamps();
  }

  _buildLights(){
    this.scene.add(new THREE.AmbientLight(0xFFF6E8, 0.75));
    const sun = new THREE.DirectionalLight(0xFFF2D8, 0.95);
    sun.position.set(10, 20, 12); sun.castShadow = true;
    sun.shadow.mapSize.set(2048, 2048);
    const s = 40;
    sun.shadow.camera.left = -s; sun.shadow.camera.right = s;
    sun.shadow.camera.top = s; sun.shadow.camera.bottom = -s;
    this.scene.add(sun);
  }

  // Open sea, running out past the fog to the horizon.
  _buildSea(){
    const geom = new THREE.PlaneGeometry(260, 200, 60, 44);
    this.waterBase = geom.attributes.position.array.slice();
    const mat = new THREE.MeshPhongMaterial({ color: C.sea, shininess: 90, specular: 0x9FD8EA });
    this.water = new THREE.Mesh(geom, mat);
    this.water.rotation.x = -Math.PI/2;
    // Sits low enough that a wave CREST still clears the sand. The sea runs on
    // underneath the beach, so a crest taller than this pokes up through the sand
    // as a stray blue triangle halfway up the shore.
    this.water.position.set(0, -0.38, -95);
    this.scene.add(this.water);
  }

  // The sand: a circle of radius GROUND_R cut off along the shoreline — an arc.
  _buildGround(){
    const XS = shoreX(GROUND_R);
    const pt = (x, z) => new THREE.Vector2(x, -z);     // world XZ -> shape XY

    const shape = new THREE.Shape();
    const N = 48;
    // the shoreline, left arm to right arm
    for (let i = 0; i <= N; i++){
      const x = -XS + (i / N) * (2 * XS);
      const p = pt(x, shoreZ(x));
      if (i === 0) shape.moveTo(p.x, p.y); else shape.lineTo(p.x, p.y);
    }
    // and back around the landward edge
    const phi0 = Math.atan2(shoreZ(XS), XS);           // right arm
    const phi1 = Math.PI - phi0;                        // left arm, the long way round
    for (let i = 0; i <= N; i++){
      const a = phi0 + (i / N) * (phi1 - phi0);
      const p = pt(Math.cos(a) * GROUND_R, Math.sin(a) * GROUND_R);
      shape.lineTo(p.x, p.y);
    }
    shape.closePath();

    const sand = new THREE.Mesh(new THREE.ShapeGeometry(shape, 12), new THREE.MeshLambertMaterial({ color: C.sand }));
    sand.rotation.x = -Math.PI/2;
    sand.receiveShadow = true;
    this.scene.add(sand);

    // surf: a band of foam riding the waterline
    const foam = new THREE.Shape();
    for (let i = 0; i <= N; i++){
      const x = -XS + (i / N) * (2 * XS);
      const p = pt(x, shoreZ(x) + 0.7);
      if (i === 0) foam.moveTo(p.x, p.y); else foam.lineTo(p.x, p.y);
    }
    for (let i = N; i >= 0; i--){
      const x = -XS + (i / N) * (2 * XS);
      const p = pt(x, shoreZ(x) - 1.3);
      foam.lineTo(p.x, p.y);
    }
    foam.closePath();
    const foamMesh = new THREE.Mesh(
      new THREE.ShapeGeometry(foam, 12),
      new THREE.MeshBasicMaterial({ color: C.foam, transparent: true, opacity: 0.55 })
    );
    foamMesh.rotation.x = -Math.PI/2;
    foamMesh.position.y = 0.03;
    this.scene.add(foamMesh);
  }

  // The landward wall. It stands ON the walk limit, so the edge of the beach is a
  // thing in the world rather than an invisible wall in an empty field.
  _buildDunes(){
    const COUNT = 104;
    const GAP = 0.13;                                  // the boardwalk's way out, at +z
    for (let i = 0; i < COUNT; i++){
      const a = (i / COUNT) * Math.PI * 2;
      if (a < GAP || a > Math.PI * 2 - GAP) continue;

      // two staggered rows, so the ridge has depth and you can't see a clean line
      // of sand over the top of it
      const row = i % 2;
      const r = DUNE_R + row * 1.7 + (Math.random() - 0.5) * 1.4;
      const x = Math.sin(a) * r;
      const z = Math.cos(a) * r;
      if (z < shoreZ(x) + 2.4) continue;               // no dunes standing in the sea

      const g = new THREE.Group();
      g.position.set(x, 0, z);
      g.rotation.y = a;
      this.scene.add(g);

      const mound = sphere(2.3 + Math.random() * 1.0, C.sand, 0, 0.1, 0, g, 12);
      mound.scale.set(1.3, 0.78 + Math.random() * 0.22, 0.9);
      mound.receiveShadow = true; mound.castShadow = true;

      // marram grass, tinted by the season like everything else that grows
      const tufts = 3 + ((Math.random() * 3) | 0);
      for (let t = 0; t < tufts; t++){
        const tx = (Math.random() - 0.5) * 2.6;
        const tz = (Math.random() - 0.5) * 1.4;
        for (let b = 0; b < 3; b++){
          const blade = box(0.07, 0.9 + Math.random() * 0.5, 0.07, this.leaf, tx, 1.05, tz, g);
          blade.rotation.z = (Math.random() - 0.5) * 0.7;
          blade.rotation.x = (Math.random() - 0.5) * 0.5;
        }
      }
    }
  }

  // The way back to town: a boardwalk running up the sand and out through the dunes.
  _buildBoardwalk(){
    const deck = new THREE.Mesh(
      new THREE.PlaneGeometry(3.6, 24),
      new THREE.MeshLambertMaterial({ color: C.rye })
    );
    deck.rotation.x = -Math.PI/2;
    deck.position.set(0, 0.02, 16);
    deck.receiveShadow = true;
    this.scene.add(deck);

    for (let z = 7; z <= 27; z += 5){
      cyl(0.1, 0.1, 0.9, C.crustDark, -1.9, 0.45, z, this.scene, 6);
      cyl(0.1, 0.1, 0.9, C.crustDark,  1.9, 0.45, z, this.scene, 6);
    }
    this._lamp(new THREE.Vector3(-2.4, 0, 22));
    this._lamp(new THREE.Vector3( 2.4, 0, 10));

    const sign = textSprite('← Toast Town', 24, '#6B4A2F', 3.6, 0.85);
    sign.position.set(0, 2.6, 26);
    this.scene.add(sign);
  }

  // The jetty — long enough to moor a ship at, which the Island will want.
  _buildJetty(){
    const g = new THREE.Group();
    g.position.set(0, 0, 0);
    this.scene.add(g);

    for (let i = 0; i < 15; i++){
      const z = -7 - i * 1.0;
      box(2.6, 0.18, 1.0, C.rye, 0, 0.55, z, g);
      cyl(0.09, 0.09, 1.4, C.crustDark, -1.1, -0.15, z, g, 6);
      cyl(0.09, 0.09, 1.4, C.crustDark,  1.1, -0.15, z, g, 6);
    }
    box(0.09, 0.55, 15.0, C.rye, -1.25, 0.9, -14.0, g);
    box(0.09, 0.55, 15.0, C.rye,  1.25, 0.9, -14.0, g);

    // mooring posts at the head
    cyl(0.22, 0.22, 1.6, C.crustDark, -1.7, 0.5, -21.6, g, 8);
    cyl(0.22, 0.22, 1.6, C.crustDark,  1.7, 0.5, -21.6, g, 8);
    this._lamp(new THREE.Vector3(1.5, 0.65, -19.5));

    const sign = textSprite('the old jetty', 24, '#6B4A2F', 3.2, 0.85);
    sign.position.set(0, 2.2, -7.6);
    this.scene.add(sign);
  }

  // A lamp that lights itself after dark, like the town's.
  _lamp(pos){
    const g = new THREE.Group();
    g.position.copy(pos);
    this.scene.add(g);
    cyl(0.12, 0.16, 0.3, C.crustDark, 0, 0.15, 0, g, 10);        // base
    cyl(0.07, 0.07, 3.4, C.cocoa, 0, 1.8, 0, g, 8);              // post
    const glass = sphere(0.26, LAMP_ON, 0, 3.6, 0, g, 12);
    glass.material = new THREE.MeshBasicMaterial({ color: LAMP_OFF });
    // distance/decay matched to the town's street lamps — the default decay over a
    // long distance spreads the light so thin it never lands on the ground
    const light = new THREE.PointLight(LAMP_ON, 0, 9, 1.7);
    light.position.set(0, 3.5, 0);
    g.add(light);
    this.lamps.push({ light, glass });
  }

  _updateLamps(){
    const night = isNight();
    this.lamps.forEach(l => {
      l.light.intensity = night ? 2.4 : 0;
      l.glass.material.color.set(night ? LAMP_ON : LAMP_OFF);
    });
  }

  _buildProps(){
    // clear of the Aquarium's footprint (x -13.3..-8.7) — a palm was growing through it
    this._palm(new THREE.Vector3(-17, 0, -2), 0.9);
    this._palm(new THREE.Vector3(10, 0, -3), -0.7);
    this._palm(new THREE.Vector3(-15, 0, 11), 0.3);
    this._palm(new THREE.Vector3(16, 0, 6), -0.4);
    this._palm(new THREE.Vector3(20, 0, -6), 0.8);

    // umbrella + towel
    const u = new THREE.Group(); u.position.set(6, 0, 0); this.scene.add(u);
    cyl(0.04, 0.04, 1.5, C.cream, 0, 0.75, 0, u, 6);
    const canopy = cyl(1.5, 0.06, 0.55, C.raspberry, 0, 1.5, 0, u, 12);
    canopy.rotation.x = -0.08;
    box(1.2, 0.04, 0.7, C.breeze, 0, 0.04, 1.1, u);

    // beach ball
    sphere(0.35, C.tangerine, -5, 0.35, 4, this.scene, 14);
    sphere(0.12, C.cream, -5, 0.35, 4.24, this.scene, 10);

    // a rowboat drawn up on the sand by the jetty. The Island is reached from here.
    const boat = new THREE.Group();
    boat.position.set(-4.6, 0, -6.2);
    boat.rotation.y = 0.5;
    this.scene.add(boat);
    const hull = box(1.5, 0.62, 3.9, C.rye, 0, 0.31, 0, boat);
    hull.castShadow = true;
    box(1.15, 0.16, 3.4, C.crustDark, 0, 0.56, 0, boat);       // the well of the boat
    box(1.2, 0.1, 0.3, C.rye, 0, 0.6, -0.8, boat);             // thwarts
    box(1.2, 0.1, 0.3, C.rye, 0, 0.6, 0.9, boat);
    const oar = cyl(0.05, 0.05, 2.6, C.cocoa, 0.5, 0.72, 0.2, boat, 6);
    oar.rotation.set(0.1, 0, 1.3);

    // rocks along the headlands, so the arms of the cove have some weight
    const ROCKS = [[-22, -9], [-24, -4], [23, -8], [25, -3], [-19, -12], [21, -12]];
    ROCKS.forEach(([x, z]) => {
      const r = sphere(0.8 + Math.random() * 0.9, C.crust, x, 0.15, z, this.scene, 8);
      r.scale.set(1, 0.62 + Math.random() * 0.3, 0.85);
      r.rotation.y = Math.random() * 3;
      r.castShadow = true; r.receiveShadow = true;
    });

    // driftwood + shells scattered up the tideline
    for (let i = 0; i < 7; i++){
      const x = (Math.random() - 0.5) * 34;
      const z = shoreZ(x) + 1.4 + Math.random() * 3.5;
      const w = box(0.28, 0.2, 1.5 + Math.random(), C.crust, x, 0.1, z, this.scene);
      w.rotation.y = Math.random() * 3;
      w.castShadow = true;
    }
  }

  // A palm you can read as a palm: a trunk that leans, and fronds that arch out and
  // droop. (They used to be flat sticks radiating from a post — green asterisks.)
  _palm(pos, rot){
    const g = new THREE.Group(); g.position.copy(pos); g.rotation.y = rot; this.scene.add(g);

    // leaning trunk, stacked so it curves
    const lean = 0.06 + Math.random() * 0.05;
    let y = 0;
    const trunk = new THREE.Group(); g.add(trunk);
    for (let i = 0; i < 6; i++){
      const seg = cyl(0.26 - i * 0.025, 0.3 - i * 0.025, 0.62, C.rye, 0, 0, 0, trunk, 8);
      seg.position.set(Math.sin(i * lean) * i * 0.13, y + 0.31, 0);
      seg.rotation.z = -i * lean;
      y += 0.58;
    }
    const topX = Math.sin(5 * lean) * 5 * 0.13;
    const crown = new THREE.Group();
    crown.position.set(topX, y + 0.1, 0);
    crown.rotation.z = -5 * lean;
    g.add(crown);
    sphere(0.22, C.cocoa, 0, 0, 0, crown, 8);                 // the nub the fronds spring from

    for (let i = 0; i < 7; i++){
      const frond = new THREE.Group();
      frond.rotation.y = (i / 7) * Math.PI * 2 + Math.random() * 0.2;
      frond.rotation.x = -0.35 - Math.random() * 0.2;      // spring upward, then arch over
      crown.add(frond);
      // Chain the segments — each one hangs off the END of the last, tilting a little
      // further down. Placing them on a straight line and rotating each in place (the
      // obvious way) leaves gaps, and the frond reads as scattered green confetti.
      let parent = frond;
      for (let s = 0; s < 5; s++){
        const seg = new THREE.Group();
        seg.position.set(0, 0, -0.58);
        seg.rotation.x = 0.26;
        parent.add(seg);
        box(0.62 - s * 0.1, 0.06, 0.64, this.leaf, 0, 0, -0.3, seg);
        parent = seg;
      }
    }
    // coconuts
    for (let i = 0; i < 3; i++){
      const a = (i / 3) * Math.PI * 2;
      sphere(0.13, C.cocoa, Math.cos(a) * 0.24, -0.16, Math.sin(a) * 0.24, crown, 8);
    }
  }

  _buildAquarium(){
    const g = new THREE.Group(); g.position.set(-11, 0, -1); this.scene.add(g);
    box(4.6, 3.4, 3.8, C.cobalt, 0, 1.7, 0, g);
    box(4.9, 0.4, 4.1, C.cobaltDim, 0, 3.5, 0, g);
    const win = cyl(1.1, 1.1, 0.2, C.breeze, 0, 1.7, 1.95, g, 24);
    win.rotation.x = Math.PI / 2;
    box(1.2, 2.2, 0.15, C.crustDark, 0, 1.1, 1.92, g);       // the door, facing the sand
    const sign = textSprite('🐠 Toast Town Aquarium', 24, '#FFF4DF', 5.0, 1.1);
    sign.position.set(-11, 4.4, -1);
    this.scene.add(sign);
    this._lamp(new THREE.Vector3(-8.4, 0, 2.2));
  }

  _makeAngler(){
    const build = (g) => {
      const body = cyl(0.3, 0.28, 0.9, C.toast, 0, 0.45, 0, g, 12);
      sphere(0.28, C.toast, 0, 1.0, 0, g, 12);
      sphere(0.06, C.limb, -0.1, 1.05, 0.22, g, 8);
      sphere(0.06, C.limb,  0.1, 1.05, 0.22, g, 8);
      cyl(0.45, 0.45, 0.08, C.cobalt, 0, 1.4, 0, g, 18);
      cyl(0.22, 0.22, 0.22, C.cobalt, 0, 1.55, 0, g, 14);
      box(0.52, 0.6, 0.36, C.sproutDim, 0, 0.5, 0.05, g);
      const rod = new THREE.Group(); rod.position.set(0.45, 0.7, 0.3); rod.rotation.z = -0.6; g.add(rod);
      cyl(0.02, 0.02, 1.8, C.cocoa, 0, 0.9, 0, rod, 6);
    };
    return new Townsperson({
      name: 'Angler Andy', line: 'the early bird gets the toastfish.',
      pos: new THREE.Vector3(3.4, 0, -7.2), facing: Math.PI * 0.85,
      wander: 0, speed: 0, bubbleY: 2.4, build
    });
  }

  _talkToAngler(){
    this.bread.stop();
    const to = new THREE.Vector3().subVectors(this.angler.root.position, this.bread.root.position);
    this.bread.facing = Math.atan2(to.x, to.z);
    this.bread.root.rotation.y = this.bread.facing;
    this.bread.drawFace('grin');
    this._renderAnglerDialog();
  }

  _renderAnglerDialog(){
    this._clearPanels();
    const { available, active } = questsForGiver('angler');
    const questRows = [];
    active.forEach(id => {
      const q = QUESTS[id];
      if (isReady(id)){
        questRows.push(`<div class="q-drow ready"><div><b>${q.title}</b><span>objective complete!</span></div>
          <button class="cafe-btn turnin" data-id="${id}">turn in ✓</button></div>`);
      } else {
        const p = objectiveProgress(id);
        questRows.push(`<div class="q-drow"><div><b>${q.title}</b><span>${p.have} / ${p.goal} ${q.objective.noun}</span></div><span class="q-dpend">in progress</span></div>`);
      }
    });
    available.forEach(id => {
      const q = QUESTS[id];
      const rew = q.reward?.crumbs ? ` · reward 🍞 ${q.reward.crumbs}` : '';
      questRows.push(`<div class="q-drow"><div><b>${q.title}</b><span>${q.desc}${rew}</span></div>
        <button class="cafe-btn accept" data-id="${id}">accept</button></div>`);
    });
    const body = questRows.length ? questRows.join('') : '<p class="q-dnone">"No bites today, but the company is good."</p>';
    const p = this._panel(`
      <div class="cafe-card q-dialog">
        <h2>Angler Andy</h2>
        <div class="cafe-sub">sandy toes beach</div>
        <div class="q-drows">${body}</div>
        <button class="cafe-btn ghost close">done</button>
      </div>`);
    p.querySelectorAll('.accept').forEach(b => b.onclick = () => { acceptQuest(b.dataset.id); this.bread.drawFace('yum'); this._renderAnglerDialog(); });
    p.querySelectorAll('.turnin').forEach(b => b.onclick = () => { turnIn(b.dataset.id); HUD.refreshCrumbs(); this._renderAnglerDialog(); });
    p.querySelector('.close').onclick = () => this._clearPanels();
  }

  _startFishing(){
    if (this.state !== 'explore') return;
    this.state = 'fishing';
    this.bread.stop();
    this.input.onTap = null;
    this.fishing = new FishingGame();
    const overlay = document.createElement('div');
    overlay.className = 'cafe-panel bare';
    document.body.appendChild(overlay);
    this.fishing.mount(overlay);
    this.fishing.onDone = (result) => {
      this.fishing.unmount();
      overlay.remove();
      this.fishing = null;
      this.state = 'explore';
      this.input.onTap = (ndc, ray) => this._tap(ray);
      HUD.refreshEnergy();
    };
    this.fishing.begin();
  }

  _applyWeather(){
    const st = getWeatherState();
    this.weather.set(st.weather, st.season);
  }

  _panel(html){
    const el = document.createElement('div');
    el.className = 'cafe-panel';
    el.innerHTML = html;
    document.body.appendChild(el);
    this.panels.push(el);
    return el;
  }
  _clearPanels(){ this.panels.forEach(p => p.remove()); this.panels = []; }

  enter({ manager }){
    this.manager = manager;
    this.input = manager.input;
    this.input.useCamera(this.camera);
    this.input.polarRange = [0.28, 0.72];
    this.input.onTap = (ndc, ray) => this._tap(ray);
    const st = getWeatherState();
    HUD.setSubtitle('sandy toes beach');
    HUD.say(`Sandy Toes Beach! ${weatherIcon(st.weather)} ${weatherName(st.weather)} · ${st.temp}°C · ${st.season}.`);
    HUD.setHint('click to walk · fish off the jetty · visit the aquarium · head back to town');
    HUD.hideHintSoon(9000);
    this._updateLamps();
  }

  exit(){ this.input.onTap = null; HUD.showEnter(null); HUD.say(''); this._clearPanels(); }

  // Keep the walker on the sand: inside the dune ridge, and landward of the surf.
  // The stopping line sits *inside* the foam band, so what halts you is the water
  // you can see breaking at your feet — not bare sand and an invisible wall.
  _clampToSand(p){
    if (p.length() > WALK_R) p.setLength(WALK_R);
    const limit = shoreZ(p.x) + 0.5;
    if (p.z < limit) p.z = limit;
    return p;
  }

  _tap(ray){
    if (checkNpcClick(ray, this.angler, this.bread)) return;

    const hit = this._raycastHotspot(ray);
    if (hit && hit.action){ hit.action(); return; }

    const dir = ray.ray.direction;
    if (Math.abs(dir.y) < 1e-4) return;
    const t = -ray.ray.origin.y / dir.y;
    if (t <= 0) return;
    const p = ray.ray.origin.clone().addScaledVector(dir, t);
    const gp = this._clampToSand(new THREE.Vector3(p.x, 0, p.z));
    HUD.hideHint();
    this.bread.walkTo(gp);
  }

  _raycastHotspot(ray){
    const dir = ray.ray.direction;
    if (Math.abs(dir.y) < 1e-4) return null;
    const t = -ray.ray.origin.y / dir.y;
    if (t <= 0) return null;
    const p = ray.ray.origin.clone().addScaledVector(dir, t);
    let best = null, bd = Infinity;
    this.hotspots.forEach(h => {
      const d = Math.hypot(p.x - h.pos.x, p.z - h.pos.z);
      if (d < 1.6 && d < bd){ bd = d; best = h; }
    });
    return best;
  }

  update(dt){
    this.bread.update(dt);
    this._clampToSand(this.bread.root.position);
    this.angler.update(dt, this.bread.root.position);

    const night = isNight();
    this.ambient.update(dt, night ? 'night' : 'day');
    this.weather.update(dt, this.bread.root.position);
    this._updateLamps();

    if (this.water){
      const pos = this.water.geometry.attributes.position.array;
      const base = this.waterBase;
      const t = performance.now() * 0.001;
      for (let i = 0; i < pos.length; i += 3){
        const x = base[i], y = base[i+1];
        pos[i+2] = Math.sin(x * 0.35 + t) * 0.16 + Math.cos(y * 0.28 + t * 0.8) * 0.11;
      }
      this.water.geometry.attributes.position.needsUpdate = true;
    }

    if (this.fishing) this.fishing.update(dt);

    let near = null, nd = Infinity;
    this.hotspots.forEach(h => { const d = h.pos.distanceTo(this.bread.root.position); if (d < nd){ nd = d; near = h; } });
    if (near && nd < near.r){
      if (this.nearSpot !== near){ this.nearSpot = near; HUD.showEnter(near.label, () => near.action()); }
    } else if (this.nearSpot){ this.nearSpot = null; HUD.showEnter(null); }

    this.azimuth += (this.input.azimuthGoal - this.azimuth) * 0.08;
    this.polar   += (this.input.polarGoal   - this.polar)   * 0.08;
    this.camTarget.lerp(new THREE.Vector3(this.bread.root.position.x * 0.5, 1.6, this.bread.root.position.z * 0.5), 0.06);
    this.camera.position.set(
      this.camTarget.x + this.camDist * Math.sin(this.azimuth) * Math.cos(this.polar),
      this.camTarget.y + this.camDist * Math.sin(this.polar),
      this.camTarget.z + this.camDist * Math.cos(this.azimuth) * Math.cos(this.polar)
    );
    this.camera.lookAt(this.camTarget);
  }

  onResize(){
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
  }
}
